import { useState, useEffect, useRef } from "react";
import TextField from "../../../audi-ui-components/TextField";
import Search from "../../../audi-ui-components/icons/Search";
import { Wrapper, Status } from "@googlemaps/react-wrapper";
import cn from "classnames";

// https://developers.google.com/maps/documentation/javascript/examples/places-autocomplete-addressform

const WrappedField = ({
  name,
  value,
  label,
  placeholder = "",
  description = "",
  onChange = () => {},
  isInvalid,
  invalidMessage,
  theme,
}) => {
  //
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [hasGeoError, setHasGeoError] = useState(false);
  const [isMissingPcode, setIsMissingPcode] = useState(false);
  const autoCompleteRef = useRef(null);
  const geocoderRef = useRef(null);
  const inputRef = useRef(null);

  const processAddressComponents = (place) => {
    let addressComponents = {
      postcode: "",
      suburb: "",
      // addressState: "",
    };
    if (place?.address_components) {
      for (let component of place.address_components) {
        let componentType = component.types[0];
        switch (componentType) {
          case "postal_code": {
            addressComponents.postcode = component.long_name;
            break;
          }
          case "locality": {
            addressComponents.suburb = component.long_name;
            break;
          }
          // case "administrative_area_level_1": {
          //   addressComponents.addressState = component.short_name;
          //   break;
          // }
        }
      }
    }
    if (!addressComponents.postcode) {
      // console.log("no postcode", addressComponents);
      setIsMissingPcode(true);
    } else {
      setIsMissingPcode(false);
    }
    return addressComponents;
  };

  const handlePlaceChanged = () => {
    let place = autoCompleteRef.current.getPlace();
    console.log(place);
    if (place && place.address_components) {
      let addressComponents = processAddressComponents(place);
      // prettier-ignore
      let searchText = addressComponents.postcode ? `${addressComponents.postcode}, ${addressComponents.suburb}` : "";
      setInputValue(searchText);
      onChange(addressComponents, searchText);
    }
  };

  const handleClickLocate = () => {
    setHasGeoError(false);
    setIsMissingPcode(false);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        // success
        // console.log("geolocation", pos.coords.latitude, pos.coords.longitude);
        let latlngObj = {
          lat: parseFloat(pos.coords.latitude),
          lng: parseFloat(pos.coords.longitude),
        };
        if (!geocoderRef.current) {
          geocoderRef.current = new google.maps.Geocoder();
        }
        geocoderRef.current
          .geocode({
            location: latlngObj,
          })
          .then((response) => {
            if (response.results?.length > 0) {
              // console.log(response);
              let addressComponents = processAddressComponents(
                response.results[0],
              );
              let searchText = `${addressComponents.postcode}, ${addressComponents.suburb}`;
              setInputValue(searchText);
              onChange(addressComponents, searchText);
            } else {
              setHasGeoError(true);
            }
          })
          .catch(() => {
            setHasGeoError(true);
          });
      },
      (error) => {
        //fail
        console.error("geolocation error", error);
        setHasGeoError(true);
      },
    );
  };

  useEffect(() => {
    autoCompleteRef.current = new google.maps.places.Autocomplete(
      inputRef.current,
      {
        componentRestrictions: { country: ["AU"] },
        fields: ["address_components", "formatted_address"],
        types: ["(regions)"],
      },
    );
    autoCompleteRef.current.addListener("place_changed", handlePlaceChanged);
    // eslint-disable-next-line
  }, []);

  return (
    <div className={cn("aui-textfield aui-textfield--floating-label aui-textfield--icon geosearch", {
      "is-focused": isFocused,
      "is-dirty": inputValue || inputValue === 0,
      "is-invalid": isInvalid,
      [`aui-theme-${theme}`]: theme
    })}>
      <div className="aui-textfield__field">
        <Search small />
        <input
          ref={inputRef}
          type="text"
          name={name}
          value={inputValue || ""}
          onChange={(e) => {
            let v = e.target.value;
            if (value) {
              onChange({}, "");
            }
            if (hasGeoError) {
              setHasGeoError(false);
            }
            if (isMissingPcode) {
              setIsMissingPcode(false);
            }
            setInputValue(v);
          }}
          onFocus={() => { setIsFocused(true); }}
          onBlur={() => {
            if (!value) {
              handlePlaceChanged();
            }
            setIsFocused(false);
          }}
          placeholder={placeholder}
          className="aui-textfield__input"
        />
        <label className="aui-textfield__label" htmlFor={name}>{label}</label>
        <span className="aui-textfield__focus-line"></span>
        <button type="button" onClick={handleClickLocate} aria-label="use your location" className="aui-button geobutton">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12.5 3.51V6.5H11.5V3.51C9.41756 3.62281 7.45016 4.50082 5.97549 5.97549C4.50082 7.45016 3.62281 9.41756 3.51 11.5H6.5V12.5H3.51C3.62281 14.5824 4.50082 16.5498 5.97549 18.0245C7.45016 19.4992 9.41756 20.3772 11.5 20.49V17.5H12.5V20.49C14.5824 20.3772 16.5498 19.4992 18.0245 18.0245C19.4992 16.5498 20.3772 14.5824 20.49 12.5H17.5V11.5H20.49C20.3772 9.41756 19.4992 7.45016 18.0245 5.97549C16.5498 4.50082 14.5824 3.62281 12.5 3.51ZM12.5 2.51C14.8475 2.624 17.0685 3.60775 18.7304 5.26963C20.3922 6.93152 21.376 9.15251 21.49 11.5H23.5V12.5H21.49C21.376 14.8475 20.3922 17.0685 18.7304 18.7304C17.0685 20.3922 14.8475 21.376 12.5 21.49V23.5H11.5V21.49C9.15251 21.376 6.93152 20.3922 5.26963 18.7304C3.60775 17.0685 2.624 14.8475 2.51 12.5H0.5V11.5H2.51C2.624 9.15251 3.60775 6.93152 5.26963 5.26963C6.93152 3.60775 9.15251 2.624 11.5 2.51V0.5H12.5V2.51Z" fill="currentColor" />
          </svg>
        </button>
      </div>
      {description && !invalidMessage && <span className="aui-textfield__description">{description}</span>}
      {invalidMessage && <span className="aui-textfield__error">{invalidMessage}</span>}
      {hasGeoError && <span className="aui-textfield__error">Sorry, we could not get your location.</span>}
      {isMissingPcode && <span className="aui-textfield__error">Please try entering a postcode instead.</span>}
    </div>
  );
};

const GeoPostcodeField = (props) => {
  return (
    <Wrapper
      apiKey={process.env.RAZZLE_GOOGLE_MAPS_API_KEY}
      libraries={["places", "geocoding"]}
      render={(status) => {
        if (status === Status.FAILURE) {
          return <TextField {...props} />;
        }
        return null;
      }}
    >
      <WrappedField {...props} />
    </Wrapper>
  );
};

export default GeoPostcodeField;
